import React, { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import BASE_URL from '../services/api';

/**
 * SessionGuard
 *
 * Wraps the app to:
 *  1. Periodically verify the JWT (every 60s). If invalid/expired, force logout.
 *  2. Intercept all fetch() calls — on any 401 response, clear localStorage and redirect to /.
 */
const SessionGuard = ({ children }) => {
  const navigate = useNavigate();
  const forcedOutRef = useRef(false);

  const forceLogout = () => {
    if (forcedOutRef.current) return;
    forcedOutRef.current = true;
    localStorage.removeItem('user');
    localStorage.removeItem('isAuthenticated');
    localStorage.removeItem('token');
    localStorage.removeItem('userName');
    localStorage.removeItem('username');
    localStorage.removeItem('userRole');
    localStorage.removeItem('portals');
    navigate('/', { replace: true });
  };

  // Global fetch interceptor — auto-logout on any 401
  useEffect(() => {
    const originalFetch = window.fetch;
    window.fetch = async (...args) => {
      const response = await originalFetch(...args);
      if (response.status === 401 && localStorage.getItem('token')) {
        forceLogout();
      }
      return response;
    };
    return () => {
      window.fetch = originalFetch;
    };
  }, []);

  // Periodic token verification (every 60s)
  useEffect(() => {
    const check = async () => {
      const token = localStorage.getItem('token');
      if (!token) return;
      try {
        const res = await fetch(`${BASE_URL}/auth/verify-token`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ token }),
        });
        if (res.status === 401 || res.status === 403) forceLogout();
      } catch (e) {
        // ignore network errors — don't kick user out for offline
      }
    };
    check(); // fire once on mount
    const id = setInterval(check, 60_000);
    return () => clearInterval(id);
  }, []);

  return children;
};

export default SessionGuard;
